import React, { createContext, useContext, useEffect, useState } from "react";
import { useServiceContext } from "./ServiceContext";
import { useAuthenticationContext } from "./UserContext";
import { Municipality } from "../core/entities/municipality";
import { TemplateTypeDisplay } from "../core/entities/templateTypeDisplay";
import { ReplacementField } from "../core/entities/replacementField";
import { LoadingScreenComponent } from "../presentation/components/state/Loading";
import { ErrorPageScreen } from "../presentation/screens/public/ErrorPage/ErrorPage.Screen";

interface ResourceContextProps {
    municipalities: Municipality[];
    replacementFields: ReplacementField[];
    templateTypes: TemplateTypeDisplay[];
}

const ResourceContext = createContext<ResourceContextProps | undefined>(
    undefined
);

interface ResourceProviderProps {
    children: React.ReactNode;
}

export function ResourceProvider({
    children
}: ResourceProviderProps): React.ReactElement {
    const { municipalityService, templateService } = useServiceContext();
    const { isSuperAdmin } = useAuthenticationContext();
    const [municipalities, setMunicipalities] = useState<Municipality[]>();
    const [replacementFields, setReplacementFields] =
        useState<ReplacementField[]>();
    const [templateTypes, setTemplateTypes] = useState<TemplateTypeDisplay[]>();
    const [error, setError] = useState<any>();

    useEffect(() => {
        if (isSuperAdmin) {
            municipalityService
                .getAll()
                .then(setMunicipalities)
                .catch(setError);
        } else {
            setMunicipalities([]);
        }
    }, [isSuperAdmin, municipalityService]);

    useEffect(() => {
        templateService
            .getTypes()
            .then((x) => setTemplateTypes(x ?? []))
            .catch(setError);
        templateService
            .getReplacementFields()
            .then((x) => setReplacementFields(x ?? []))
            .catch(setError);
    }, [templateService]);

    const isLoading =
        municipalities === undefined ||
        replacementFields === undefined ||
        templateTypes === undefined;
    return (
        <ResourceContext.Provider
            value={{
                municipalities: municipalities!,
                replacementFields: replacementFields!,
                templateTypes: templateTypes!
            }}
        >
            {isLoading && !error ? (
                <LoadingScreenComponent />
            ) : error ? (
                <ErrorPageScreen error={error} />
            ) : (
                children
            )}
        </ResourceContext.Provider>
    );
}

export function useResourceContext(): ResourceContextProps {
    const context = useContext(ResourceContext);
    if (context === undefined) {
        throw new Error(
            "useResourceContext must be used within a ServiceProvider"
        );
    }
    return context;
}
